"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive, Handle } from "vaul";

import { cn } from "@/utils/lib";

type DrawerDirection = NonNullable<
  React.ComponentProps<typeof DrawerPrimitive.Root>["direction"]
>;

type CSSPropertiesWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type DrawerControlContextValue = {
  close: () => void;
  direction: DrawerDirection;
};

const DrawerControlContext =
  React.createContext<DrawerControlContextValue | null>(null);

const FALLBACK_DRAG_EXIT_CLEANUP_MS = 550;

const Drawer = ({
  defaultOpen = false,
  direction = "bottom",
  handleOnly = true,
  nested = false,
  onOpenChange,
  open: openProp,
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  const parentDrawerControl = React.useContext(DrawerControlContext);
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const shouldUseNestedRoot = nested && parentDrawerControl !== null;
  const DrawerRoot = shouldUseNestedRoot
    ? DrawerPrimitive.NestedRoot
    : DrawerPrimitive.Root;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return (
    <DrawerControlContext.Provider
      value={{
        close: () => handleOpenChange(false),
        direction,
      }}
    >
      <DrawerRoot
        direction={direction}
        handleOnly={handleOnly}
        nested={shouldUseNestedRoot ? undefined : nested}
        open={open}
        onOpenChange={handleOpenChange}
        shouldScaleBackground={shouldScaleBackground}
        {...props}
      />
    </DrawerControlContext.Provider>
  );
};
Drawer.displayName = "Drawer";

type DrawerTriggerProps = React.ComponentProps<
  typeof DrawerPrimitive.Trigger
> & {
  asChild?: boolean;
  nativeButton?: boolean;
  render?: React.ReactNode;
};

const DrawerTrigger = ({
  asChild,
  children,
  nativeButton,
  render,
  ...props
}: DrawerTriggerProps) => {
  void nativeButton;

  return (
    <DrawerPrimitive.Trigger asChild={!!render || asChild} {...props}>
      {render ?? children}
    </DrawerPrimitive.Trigger>
  );
};
DrawerTrigger.displayName = "DrawerTrigger";

const DrawerPortal = DrawerPrimitive.Portal;

type DrawerCloseProps = React.ComponentProps<typeof DrawerPrimitive.Close> & {
  asChild?: boolean;
  nativeButton?: boolean;
  render?: React.ReactNode;
};

const DrawerClose = ({
  asChild,
  children,
  nativeButton,
  render,
  ...props
}: DrawerCloseProps) => {
  void nativeButton;

  return (
    <DrawerPrimitive.Close asChild={!!render || asChild} {...props}>
      {render ?? children}
    </DrawerPrimitive.Close>
  );
};
DrawerClose.displayName = "DrawerClose";

export type DrawerContentChrome = "default" | "ghost";
type DrawerScrollOwner = "content" | "body";

const DrawerOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
  ref?: React.Ref<React.ElementRef<typeof DrawerPrimitive.Overlay>>;
}) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-100 bg-black/80 backdrop-blur-xs data-[state=closed]:pointer-events-none",
      className,
    )}
    {...props}
  />
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = ({
  ref,
  bodyClassName,
  chrome = "default",
  className,
  children,
  scrollOwner = "body",
  style,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  bodyClassName?: string;
  chrome?: DrawerContentChrome;
  ref?: React.Ref<React.ElementRef<typeof DrawerPrimitive.Content>>;
  scrollOwner?: DrawerScrollOwner;
}) => {
  const drawerControl = React.useContext(DrawerControlContext);
  const bodyOwnsScroll = chrome !== "ghost" && scrollOwner === "body";
  const keyboardAwareStyle: CSSPropertiesWithVars = {
    "--overlay-keyboard-inset-bottom":
      "var(--visual-keyboard-inset-bottom, 0px)",
    "--overlay-keyboard-offset-top": "var(--visual-keyboard-offset-top, 0px)",
    "--overlay-keyboard-available-height":
      "calc(100dvh - var(--overlay-keyboard-inset-bottom) - var(--overlay-keyboard-offset-top))",
    "--drawer-content-max-height":
      "min(90vh, calc(var(--overlay-keyboard-available-height) - 24px))",
    "--overlay-content-max-height": "var(--drawer-content-max-height)",
    ...style,
  };
  const fallbackDragStateRef = React.useRef<{
    active: boolean;
    drawerEl: HTMLElement;
    pointerId: number;
    startX: number;
    startY: number;
    startTime: number;
  } | null>(null);

  const getCloseDelta = React.useCallback(
    (pointerX: number, pointerY: number, startX: number, startY: number) => {
      switch (drawerControl?.direction) {
        case "top":
          return startY - pointerY;
        case "left":
          return startX - pointerX;
        case "right":
          return pointerX - startX;
        case "bottom":
        default:
          return pointerY - startY;
      }
    },
    [drawerControl?.direction],
  );

  const setFallbackTransform = React.useCallback(
    (drawerEl: HTMLElement, delta: number) => {
      const safeDelta = Math.max(delta, 0);

      switch (drawerControl?.direction) {
        case "top":
          drawerEl.style.transform = `translate3d(0, ${safeDelta * -1}px, 0)`;
          return;
        case "left":
          drawerEl.style.transform = `translate3d(${safeDelta * -1}px, 0, 0)`;
          return;
        case "right":
          drawerEl.style.transform = `translate3d(${safeDelta}px, 0, 0)`;
          return;
        case "bottom":
        default:
          drawerEl.style.transform = `translate3d(0, ${safeDelta}px, 0)`;
      }
    },
    [drawerControl?.direction],
  );

  const resetFallbackTransform = React.useCallback((drawerEl: HTMLElement) => {
    drawerEl.style.transition = "";
    drawerEl.style.transform = "";
  }, []);

  const finishFallbackDrag = React.useCallback(
    (pointerX?: number, pointerY?: number) => {
      const dragState = fallbackDragStateRef.current;
      fallbackDragStateRef.current = null;

      if (
        !dragState ||
        !drawerControl ||
        pointerX === undefined ||
        pointerY === undefined
      ) {
        return;
      }

      if (!dragState.active) {
        return;
      }

      const closeDelta = getCloseDelta(
        pointerX,
        pointerY,
        dragState.startX,
        dragState.startY,
      );
      const elapsedMs = Math.max(performance.now() - dragState.startTime, 1);
      const velocity = closeDelta / elapsedMs;
      const { height, width } = dragState.drawerEl.getBoundingClientRect();
      const drawerSize =
        drawerControl.direction === "left" ||
        drawerControl.direction === "right"
          ? width
          : height;
      const closeThreshold = Math.min(Math.max(drawerSize * 0.22, 96), 180);

      if (closeDelta >= closeThreshold || velocity >= 0.65) {
        dragState.drawerEl.style.transition = "";
        drawerControl.close();

        window.setTimeout(() => {
          resetFallbackTransform(dragState.drawerEl);
        }, FALLBACK_DRAG_EXIT_CLEANUP_MS);

        return;
      }

      dragState.drawerEl.style.transition = "transform 180ms ease-out";
      dragState.drawerEl.style.transform = "";

      window.setTimeout(() => {
        resetFallbackTransform(dragState.drawerEl);
      }, 180);
    },
    [drawerControl, getCloseDelta, resetFallbackTransform],
  );

  return (
    <DrawerPortal>
      <DrawerOverlay
        className={
          chrome === "ghost" ? "bg-black/10 backdrop-blur-2xl" : undefined
        }
      />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          chrome === "ghost"
            ? `
                fixed inset-x-0 top-0 bottom-(--overlay-keyboard-inset-bottom)
                z-100 flex touch-pan-y! flex-col overflow-y-auto
                overscroll-contain bg-transparent outline-hidden
                [-webkit-overflow-scrolling:touch] data-[state=closed]:pointer-events-none
              `
            : bodyOwnsScroll
              ? `
                  fixed inset-x-0 bottom-(--overlay-keyboard-inset-bottom)
                  z-100 mt-24 flex h-auto max-h-(--drawer-content-max-height)
                  flex-col overflow-hidden rounded-t-[10px] bg-background
                  outline-hidden data-[state=closed]:pointer-events-none
                `
              : `
                fixed inset-x-0 bottom-(--overlay-keyboard-inset-bottom)
                z-100 mt-24 flex h-auto max-h-(--drawer-content-max-height)
                flex-col overflow-y-auto rounded-t-[10px] bg-background
                outline-hidden data-[state=closed]:pointer-events-none
              `,
        )}
        data-vaul-custom-container="true"
        style={keyboardAwareStyle}
        {...props}
      >
        <div
          className={cn(
            chrome === "ghost" && "min-h-full",
            bodyOwnsScroll && "flex min-h-0 flex-1 flex-col",
            !bodyOwnsScroll && bodyClassName,
            !bodyOwnsScroll && className,
          )}
        >
          {chrome !== "ghost" && (
            <div
              onPointerDownCapture={(ev) => {
                const drawerEl = (ev.target as HTMLElement).closest(
                  "[data-vaul-drawer]",
                );

                if (!drawerEl || !(drawerEl instanceof HTMLElement)) {
                  fallbackDragStateRef.current = null;
                  return;
                }

                fallbackDragStateRef.current = {
                  active: false,
                  drawerEl,
                  pointerId: ev.pointerId,
                  startX: ev.pageX,
                  startY: ev.pageY,
                  startTime: performance.now(),
                };
              }}
              onPointerMoveCapture={(ev) => {
                const dragState = fallbackDragStateRef.current;

                if (
                  !dragState ||
                  dragState.pointerId !== ev.pointerId ||
                  !drawerControl
                ) {
                  return;
                }

                const closeDelta = getCloseDelta(
                  ev.pageX,
                  ev.pageY,
                  dragState.startX,
                  dragState.startY,
                );

                if (closeDelta < 24) {
                  return;
                }

                const computedTransform = getComputedStyle(
                  dragState.drawerEl,
                ).transform;

                if (
                  !dragState.active &&
                  (dragState.drawerEl.classList.contains("vaul-dragging") ||
                    computedTransform !== "none")
                ) {
                  fallbackDragStateRef.current = null;
                  return;
                }

                dragState.active = true;
                dragState.drawerEl.style.transition = "none";
                setFallbackTransform(dragState.drawerEl, closeDelta);
              }}
              onPointerUpCapture={(ev) => {
                finishFallbackDrag(ev.pageX, ev.pageY);
              }}
              onPointerCancelCapture={() => {
                const dragState = fallbackDragStateRef.current;
                fallbackDragStateRef.current = null;

                if (dragState?.active) {
                  resetFallbackTransform(dragState.drawerEl);
                }
              }}
            >
              <Handle
                className="
                  sticky! top-0 z-100 -mt-px flex! h-auto! w-full!
                  place-content-center! rounded-none! border-0! bg-transparent! p-4!
                  opacity-100! backdrop-blur-sm
                "
              >
                <div
                  className="
                    mx-auto! my-4! h-2! w-[100px]! shrink-0! rounded-full! bg-muted!
                  "
                />
              </Handle>
            </div>
          )}
          {bodyOwnsScroll ? (
            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto",
                bodyClassName,
                className,
              )}
            >
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
};
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        grid gap-1.5 p-4 text-center
        sm:text-left
      `,
      className,
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
  ref?: React.Ref<React.ElementRef<typeof DrawerPrimitive.Title>>;
}) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      `
        py-2 text-center font-sans text-lg leading-none font-semibold
        tracking-tight
      `,
      className,
    )}
    {...props}
  />
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & {
  ref?: React.Ref<React.ElementRef<typeof DrawerPrimitive.Description>>;
}) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
